import { useState, useCallback, useEffect } from "react";
import { useDisclosure, useWindowEvent } from "@mantine/hooks";

export interface ImagePreviewState {
    /** Whether the preview modal is open */
    opened: boolean;
    /** Current scale factor of the image */
    scale: number;
    /** Current rotation angle of the image in degrees */
    rotation: number;
    /** Current translation coordinates of the image */
    translate: { x: number; y: number };
    /** Whether the image is currently being dragged */
    isDragging: boolean;
}

export interface ImagePreviewHandlers {
    /** Opens the preview modal */
    open: () => void;
    /** Closes the preview modal */
    close: () => void;
    /** Increases the scale of the image */
    zoomIn: () => void;
    /** Decreases the scale of the image */
    zoomOut: () => void;
    /** Rotates the image by 90 degrees */
    rotate: () => void;
    /** Resets the image transformation to default values */
    reset: () => void;
    /** Sets the dragging state */
    setIsDragging: (value: boolean) => void;
    /** Updates the translation of the image */
    setTranslate: (updater: (t: { x: number; y: number }) => { x: number; y: number }) => void;
    /** Sets the last recorded mouse position */
    setLastMousePos: (pos: { x: number; y: number }) => void;
    /** The last recorded mouse position */
    lastMousePos: { x: number; y: number };
}

/**
 * A hook that manages the transformation state (zoom, rotation, translation) for an image preview.
 * Includes hotkeys and mouse wheel zoom support.
 *
 * Note: Escape key handling is handled in the ImagePreview component using
 * local DOM event propagation control to support nesting in other modals.
 *
 * @returns An object containing the current state and interaction handlers.
 */
export const useImagePreview = () => {
    const [opened, { open, close }] = useDisclosure(false);
    const [scale, setScale] = useState(0.9);
    const [rotation, setRotation] = useState(0);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });

    const zoomIn = useCallback(() => setScale((s) => Math.min(s + 0.2, 5)), []);
    const zoomOut = useCallback(() => setScale((s) => Math.max(s - 0.2, 0.5)), []);
    const rotate = useCallback(() => setRotation((r) => r + 90), []);
    const reset = useCallback(() => {
        setScale(0.9);
        setRotation(0);
        setTranslate({ x: 0, y: 0 });
    }, []);

    // Automatically reset translation when the image is zoomed out to base scale
    // This prevents the image from being "stuck" off-center when dragging is disabled.
    useEffect(() => {
        if (scale <= 1) {
            setTranslate({ x: 0, y: 0 });
        }
    }, [scale]);

    /**
     * Handle Escape key to close the preview.
     * Since interactive elements have data-mantine-stop-propagation="true",
     * parent modals' Mantine listeners will ignore this event, but we can
     * still react to it here.
     */
    useWindowEvent("keydown", (e) => {
        if (e.key === "Escape" && opened) {
            close();
        }
    });

    const state: ImagePreviewState = {
        opened,
        scale,
        rotation,
        translate,
        isDragging,
    };

    const handlers: ImagePreviewHandlers = {
        open,
        close,
        zoomIn,
        zoomOut,
        rotate,
        reset,
        setIsDragging,
        setTranslate,
        setLastMousePos,
        lastMousePos,
    };

    return { state, handlers };
};
